import { useState, useEffect } from 'react';
import { X, Link as LinkIcon, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Invoice {
  id: string;
  supplier: string;
  invoice_number: string;
  invoice_date: string;
  total_amount: number;
  description: string;
}

interface LinkInvoiceModalProps {
  fundCallId: string;
  fundCallName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function LinkInvoiceModal({ fundCallId, fundCallName, onClose, onSuccess }: LinkInvoiceModalProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [allocatedAmount, setAllocatedAmount] = useState('');
  const [justificationNote, setJustificationNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvoices();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = invoices.filter(inv =>
        inv.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.invoice_number && inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (inv.description && inv.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredInvoices(filtered);
    } else {
      setFilteredInvoices(invoices);
    }
  }, [searchTerm, invoices]);

  const loadInvoices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get all invoices not already linked to this fund call
      const { data: linkedInvoices } = await supabase
        .from('grant_fund_call_invoices')
        .select('invoice_id')
        .eq('fund_call_id', fundCallId);

      const linkedIds = linkedInvoices?.map(li => li.invoice_id) || [];

      const { data, error } = await supabase
        .from('grant_invoices')
        .select('*')
        .eq('user_id', user.id)
        .not('id', 'in', `(${linkedIds.join(',')})`)
        .order('invoice_date', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
      setFilteredInvoices(data || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceSelect = (invoice: Invoice) => {
    setSelectedInvoiceId(invoice.id);
    // Auto-suggest total amount minus already allocated
    setAllocatedAmount(invoice.total_amount.toString());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedInvoiceId) {
      setError('Veuillez sélectionner une facture');
      return;
    }

    if (!allocatedAmount || parseFloat(allocatedAmount) <= 0) {
      setError('Le montant affecté doit être supérieur à 0');
      return;
    }

    setSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      const { error: linkError } = await supabase
        .from('grant_fund_call_invoices')
        .insert({
          user_id: user.id,
          fund_call_id: fundCallId,
          invoice_id: selectedInvoiceId,
          allocated_amount: parseFloat(allocatedAmount),
          justification_note: justificationNote.trim() || null,
        });

      if (linkError) throw linkError;

      onSuccess();
    } catch (err: any) {
      console.error('Error linking invoice:', err);
      setError(err.message || 'Erreur lors de la liaison');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const selectedInvoice = invoices.find(inv => inv.id === selectedInvoiceId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Lier une facture existante</h2>
            <p className="text-sm text-gray-600 mt-1">ADF: {fundCallName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une facture..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Invoice list */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              {searchTerm ? 'Aucune facture trouvée' : 'Aucune facture disponible'}
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  onClick={() => handleInvoiceSelect(invoice)}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedInvoiceId === invoice.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{invoice.supplier}</div>
                      <div className="text-sm text-gray-600">
                        {invoice.invoice_number && `N° ${invoice.invoice_number} • `}
                        {formatDate(invoice.invoice_date)}
                      </div>
                      {invoice.description && (
                        <div className="text-sm text-gray-500 mt-1">{invoice.description}</div>
                      )}
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(invoice.total_amount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Link form */}
          {selectedInvoice && (
            <form onSubmit={handleSubmit} className="space-y-4 border-t border-gray-200 pt-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-sm font-medium text-emerald-900 mb-2">Facture sélectionnée</div>
                <div className="text-sm text-emerald-800">
                  {selectedInvoice.supplier} - {formatCurrency(selectedInvoice.total_amount)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant affecté de cet ADF (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={allocatedAmount}
                  onChange={(e) => setAllocatedAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Indiquez la part de cette facture financée par cet ADF
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note de justification
                </label>
                <textarea
                  value={justificationNote}
                  onChange={(e) => setJustificationNote(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Ex: Acompte 30%, Solde, etc."
                />
              </div>
            </form>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedInvoiceId || submitting}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <LinkIcon className="w-5 h-5" />
            {submitting ? 'Liaison...' : 'Lier la facture'}
          </button>
        </div>
      </div>
    </div>
  );
}
